import type React from "react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Paper,
  TablePagination,
  Typography,
} from "@mui/material";

import { IContactData, VenueData } from "../types/venues.types";
import { useVenueData } from "../view-venue/hooks/UseVenueData";
import apiService from "../../../service/Api";

import { useContactSearch } from "../view-venue/hooks/UseContactSearch";
import {
  DOCUMENT_TYPES,
  mapContactDataToVenueContact,
} from "../view-venue/utils/venue.utils";
import LoadingState from "../view-venue/components/LoadingState";
import { ErrorState } from "../view-venue/components/ErrorState";

import InfoCard from "../view-venue/components/InfoCard";
import formatDate from "../../../utils/FormatDate";
import ViewVenuePhotos from "../tabs/ViewVenuePhotos";

import ViewVenueV2Dashboard from "./components/VenuesV2Dashboard";
import { People } from "@mui/icons-material";
import CompactContactCard from "./components/CompactContactCard";
import BookingsCardV2 from "./components/BookingsCardV2";
import { RootState } from "../../../store/Index";
import { useSelector } from "react-redux";
import RoomDetails from "./components/RoomDetailsTab";
import { RoomApiData } from "../../../types/venue";
import { VenueFooter } from "../view-venue/components/VenueFooter";
import AddOtherContactsModal from "./components/AddOtherContacts";
import AllFloorPlans from "./components/AllFloorPlans";
import VenueGallery from "./components/VenueGallery";
import SiteVisitAiFloatingChat from "../../../components/agent/SiteVisitAiFloatingChat";

interface ViewVenueV2Props {
  venueData?: VenueData;
}

const ViewVenueV2: React.FC<ViewVenueV2Props> = ({
  venueData: defaultVenueData,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [notePage, setNotePage] = useState(0);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Contact pagination
  const [contactPage, setContactPage] = useState(0);
  const [contactRowsPerPage, setContactRowsPerPage] = useState(6);
  const [openAddContactModal, setOpenAddContactModal] = useState(false);
  // ── Data hooks ──────────────────────────────
  const { venueData, setVenueData, loading, error } = useVenueData({
    id,
    defaultVenueData,
    apiService,
  });

  const refetchVenue = async () => {
    if (!id) return;
    try {
      const response = await apiService.get<VenueData>(
        `/api/venues/v1/venue/${id}/`,
      );
      setVenueData({
        ...response.data,
        files: response.data.files.map((file) => ({
          ...file,
          venue_id: file.venue ?? response.data.id,
        })),
        rooms: (response.data.children as unknown as RoomApiData[]) ?? [],
      });
    } catch {
      // silent — user already sees success toast from upload
    }
  };
  const [bookingsPage, setBookingsPage] = useState(0);
  const [bookingsRowsPerPage, setBookingsRowsPerPage] = useState(5);

  const handleBookingsPageChange = (_: unknown, newPage: number) =>
    setBookingsPage(newPage);

  const handleBookingsRowsPerPageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setBookingsRowsPerPage(parseInt(e.target.value, 10));
    setBookingsPage(0);
  };

  const mergedBookings = Array.from(
    new Map(
      [
        ...(venueData?.venue_bookings ?? []),
        ...(venueData?.children ?? []).flatMap((c) => c.venue_bookings ?? []),
      ].map((b) => [b.id, b]),
    ).values(),
  ).sort((a, b) => {
    const dateA = a.services?.[0]?.date_of_service ?? "";
    const dateB = b.services?.[0]?.date_of_service ?? "";
    return dateB.localeCompare(dateA);
  });

  const {
    openContactModal,
    setOpenContactModal,
    contactSearch,
    setContactSearch,
    searchResults,
    isSearching,
    savingContact,
    contactModalStep,
    setContactModalStep,
    searchAttempted,
    newContactForm,
    setNewContactForm,
    handleAddExistingContact,
    handleAddNewContact,
    resetContactModal,
  } = useContactSearch({ venueId: id, venueData, setVenueData, apiService });

  // ── Contact handlers ────────────────────────
  const handleChangeContactPage = (_: unknown, newPage: number) =>
    setContactPage(newPage);

  const handleChangeContactRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setContactRowsPerPage(Number.parseInt(e.target.value, 10));
    setContactPage(0);
  };

  const paginatedContacts = (venueData?.contacts || []).slice(
    contactPage * contactRowsPerPage,
    contactPage * contactRowsPerPage + contactRowsPerPage,
  );

  const handleContactRemove = (contactId: number) => {
    if (venueData) {
      setVenueData({
        ...venueData,
        contacts: venueData.contacts.filter((c) => c.id !== contactId),
      });
    }
  };

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const handleContactEdit = (updatedContact: IContactData) => {
    if (venueData) {
      const venueId = Number.parseInt(id || "0");
      const normalizedContact = mapContactDataToVenueContact(
        updatedContact,
        venueId,
      );
      setVenueData({
        ...venueData,
        contacts: venueData.contacts.map((c) =>
          c.id === updatedContact.id ? normalizedContact : c,
        ),
      });
    }
  };

  // ── Guard states ────────────────────────────
  if (loading) return <LoadingState />;
  if (error || !venueData)
    return <ErrorState error={error} onBackClick={() => navigate("/venues")} />;
  const rooms: RoomApiData[] = venueData.rooms ?? [];

  // ── Derived data ────────────────────────────
  // ── Derived data ────────────────────────────

  const primaryContact =
    venueData.contacts?.find((c) =>
      c.role?.toLowerCase().includes("manager"),
    ) ||
    venueData.contacts?.[0] ||
    null;
  const galleryPhotos =
    venueData.files?.filter((file) => file.file_type === "venue_photos") ?? [];
  const coverPhoto =
    galleryPhotos.find((file) => file.is_cover)?.file ??
    galleryPhotos[0]?.file ??
    null;

  const healthScore = venueData.completeness_score ?? 0;

  const notes: string[] = venueData.notes
    ? Array.isArray(venueData.notes)
      ? venueData.notes
      : [venueData.notes]
    : [];
  const currentNote = notes[notePage] ?? null;

  // ── ContactDetailsDialog bound to this venue ─

  // ── Shared props for VenueDetailsTab ─────────

  // ── Tab content map (tabs 2–6; tabs 0 "General" and 1 "Details" are rendered directly in Dashboard) ──
  const tabContent: Record<number, React.ReactNode> = {
    0: (
      <Box>
        {DOCUMENT_TYPES.filter(
          (t) =>
            t.toLowerCase().includes("room") &&
            !t.toLowerCase().includes("floor_plan"),
        ).map((docType) => (
          <ViewVenuePhotos
            key={docType}
            files={venueData.files.filter((f) => f.file_type !== "floor_plan")}
            document_type={docType}
          />
        ))}
      </Box>
    ),
    2: (
      <RoomDetails
        rooms={rooms}
        onEditClick={(room) => console.log("Edit room:", room)}
      />
    ),

    // Floor Plans
    3: (
      <AllFloorPlans
        venueData={{
          id: venueData.id,
          name: venueData.name,
          files: venueData.files ?? [],
          children: venueData.children ?? [],
        }}
        onRefresh={refetchVenue}
      />
    ),

    // Gallery

    4: (
      <VenueGallery
        files={venueData.files}
        venueId={venueData.id}
        onRefresh={refetchVenue}
      />
    ),

    // Bookings
    5: (
      <Box>
        <BookingsCardV2
          bookings={mergedBookings.slice(
            bookingsPage * bookingsRowsPerPage,
            bookingsPage * bookingsRowsPerPage + bookingsRowsPerPage,
          )}
          totalBookings={mergedBookings.length}
          bookingsLoading={false}
          page={bookingsPage}
          rowsPerPage={bookingsRowsPerPage}
          onPageChange={handleBookingsPageChange}
          onRowsPerPageChange={handleBookingsRowsPerPageChange}
          onBookingClick={(bookingId) => navigate(`/bookings/${bookingId}`)}
          formatDate={formatDate}
          InfoCard={InfoCard}
        />
      </Box>
    ),

    // Contacts
    6: (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: "none" }}
            onClick={() => setOpenAddContactModal(true)}
          >
            Add Contact
          </Button>
        </Box>

        {paginatedContacts.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            py={4}
          >
            No contacts added yet.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 2,
            }}
          >
            {paginatedContacts.map((contact) => (
              <CompactContactCard
                key={contact.id}
                contact={contact as unknown as IContactData}
                onEdit={handleContactEdit}
                onRemove={handleContactRemove}
                venueData={venueData}
                variant="tab"
              />
            ))}
          </Box>
        )}

        {(venueData.contacts?.length ?? 0) > contactRowsPerPage && (
          <TablePagination
            component="div"
            count={venueData.contacts?.length ?? 0}
            page={contactPage}
            rowsPerPage={contactRowsPerPage}
            onPageChange={handleChangeContactPage}
            onRowsPerPageChange={handleChangeContactRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Box>
    ),
  };

  // ── Render ───────────────────────────────────
  return (
    <Box>
      <ViewVenueV2Dashboard
        // Layout state
        activeTab={activeTab}
        onTabChange={setActiveTab}
        notePage={notePage}
        onNotePrev={() => setNotePage((p) => p - 1)}
        onNoteNext={() => setNotePage((p) => p + 1)}
        // Venue data
        venueData={venueData}
        coverPhoto={coverPhoto}
        healthScore={healthScore}
        primaryContact={primaryContact}
        notes={notes}
        currentNote={currentNote}
        totalBookings={mergedBookings.length}
        formatDate={formatDate}
        // Tab content
        tabContent={tabContent}
        // Navigation
        onEditClick={() => navigate(`/venues/view/${id}/edit`)}
        onSiteVisitClick={() => navigate(`/venues/view/${id}/site-visit`)}
        onEditNoteClick={() => navigate(`/venues/view/${id}/edit`)}
        // Contact sidebar
        onContactRemove={handleContactRemove}
        onAddContactClick={() => setOpenContactModal(true)}
        // Add Contact Modal
        openContactModal={openContactModal}
        onContactModalClose={resetContactModal}
        contactModalStep={contactModalStep}
        setContactModalStep={setContactModalStep}
        contactSearch={contactSearch}
        setContactSearch={setContactSearch}
        searchResults={searchResults}
        isSearching={isSearching}
        savingContact={savingContact}
        searchAttempted={searchAttempted}
        newContactForm={newContactForm}
        setNewContactForm={setNewContactForm}
        onAddExistingContact={handleAddExistingContact}
        onAddNewContact={handleAddNewContact}
        onPhotosChange={refetchVenue}
        onNoteUpdate={(note) => {
          if (!venueData) return;

          setVenueData({
            ...venueData,
            notes: note,
          });
        }}
        contactCard={
          primaryContact ? (
            <CompactContactCard
              contact={primaryContact as unknown as IContactData}
              onEdit={handleContactEdit}
              onRemove={handleContactRemove}
              venueData={venueData}
              variant="sidebar"
            />
          ) : (
            <Paper
              variant="outlined"
              sx={{
                height: 180,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: reduxTheme.primary_colors?.[800],
                  color: "text.disabled",
                }}
              >
                <People />
              </Avatar>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                No contacts yet
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1, textTransform: "none", fontSize: 11 }}
                onClick={() => setOpenAddContactModal(true)}
              >
                Add Contact
              </Button>
            </Paper>
          )
        }
      />
      <AddOtherContactsModal
        open={openAddContactModal}
        onClose={() => setOpenAddContactModal(false)}
        savingContact={savingContact}
        newContactForm={newContactForm}
        setNewContactForm={setNewContactForm}
        onAddExistingContact={handleAddExistingContact} // 👈 was onAddExistingContact
        onAddNewContact={() => {
          handleAddNewContact(); // 👈 was onAddNewContact
          setOpenAddContactModal(false);
        }}
      />
      <VenueFooter venueData={venueData} />
      <SiteVisitAiFloatingChat venueData={venueData} />
    </Box>
  );
};

export default ViewVenueV2;
